import { Component, inject, OnInit } from '@angular/core';
import { ChartType, GoogleChartsModule } from 'angular-google-charts';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { DataService } from '@services/contests-data.service';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { NzTableModule } from 'ng-zorro-antd/table';
import { PlanService } from '@services/plan.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexStroke,
  ApexMarkers,
  ApexGrid,
  ApexDataLabels,
  ApexTooltip,
  ApexLegend,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  isArabicLang,
  ensureArabicFont,
  reshapeArabic,
  containsArabic,
  arabicAwareCellHook,
  drawBilingualText,
} from '../../../utils/arabic-pdf.util';
declare module 'file-saver';

export type LineChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  markers: ApexMarkers;
  colors: string[];
  grid: ApexGrid;
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
  legend: ApexLegend;
};
export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke?: ApexStroke;
  markers?: ApexMarkers;
  colors?: string[];
  grid?: ApexGrid;
  dataLabels?: ApexDataLabels;
  tooltip?: ApexTooltip;
  legend?: ApexLegend;
  plotOptions?: any;
  yaxis?: any;
  fill?: any;
};
@Component({
  selector: 'app-analysis',
  standalone: true,
  imports: [
    CommonModule,
    GoogleChartsModule,
    TranslateModule,
    NzDropDownModule,
    RouterLink,
    TranslateModule,
    SharedModule,
    NzTableModule,
    NgApexchartsModule,
    NzToolTipModule,
  ],
  templateUrl: './analysis.component.html',
  styleUrls: ['./analysis.component.scss'],
})
export class AnalysisComponent implements OnInit {
  planType: 'none' | 'basic' | 'advanced' = 'none';
  selectedPlan: any = {};
  activePercentage: number = 0;
  analysis: any[] = [];
  listOfData: any = [];
  selectedTimeFilter: 'week' | 'month' | 'year' = 'week';
  timeFilterLabel: string = 'This week';
  fullReportData: any[] = [];
  Math = Math;
  previousData: any = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    totalViews: 0,
    totalClicks: 0
  };

  http = inject(HttpClient);
  dataService = inject(DataService);
  pageService = inject(PagesService);
  sharedService = inject(SharedService);
  planService = inject(PlanService);
  router = inject(Router);

  // ==================== GETTER FOR DISPLAY DATA ====================
  get displayData(): any[] {
    return this.fullReportData && this.fullReportData.length > 0
      ? this.fullReportData
      : this.listOfData;
  }
  ngOnInit(): void {
    this.analysis = [
      {
        title: 'analytic2.totalContest',
        number: 0,
        percentage: 0,
        progressText: 'analytic2.progressText',
        icon: 'assets/images/participant.svg',
        tooltip: 'Shows total views of the contest',
      },
      {
        title: 'analytic2.activeContests',
        number: 0,
        percentage: 0,
        progressText: 'analytic2.progressText',
        icon: 'assets/images/view.svg',
        tooltip: 'Total Active joined',
      },
      {
        title: 'analytic2.pending',
        number: 0,
        percentage: 0,
        progressText: 'analytic2.progressText',
        icon: 'assets/images/clicks.svg',
        tooltip: 'Pending Participate',
      },
      {
        title: 'analytic2.totalViews',
        number: 0,
        percentage: 0,
        progressText: 'analytic2.progressText',
        icon: 'assets/images/visitor.svg',
        tooltip: 'Total View recorded',
      },
      {
        title: 'analytic2.totalClicks',
        number: 0,
        percentage: 0,
        progressText: 'analytic2.progressText',
        icon: 'assets/images/pending.svg',
        tooltip: 'Total clicks recorded',
      },
    ];
    this.selectedPlan = this.planService.getPlan();
    // Initialize with default year filter
    const dateRange = this.getDateRange(this.selectedTimeFilter);
    this.getReport(dateRange.start_date, dateRange.end_date);
    this.sharedService.getPlanTypeObservable().subscribe((type) => {
      this.planType = type;
    });
  }

  barChart: Partial<ChartOptions> = {
    series: [],
    chart: {
      type: 'bar',
      height: 400,
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
      dropShadow: {
        enabled: true,
        top: 3,
        left: 0,
        blur: 4,
        opacity: 0.15,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
        endingShape: 'rounded',
        borderRadius: 8,
        borderRadiusApplication: 'end',
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -25,
      style: { fontSize: '11px', fontWeight: '600', colors: ['#304758'] },
      formatter: (val: number) => val.toFixed(0),
    },
    stroke: { show: true, width: 3, colors: ['transparent'] },
    xaxis: {
      categories: [],
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: { fontSize: '11px', fontWeight: 500, colors: '#546E7A' },
        offsetY: 5,
      },
      axisBorder: { show: true, color: '#E0E0E0' },
      axisTicks: { show: true, color: '#E0E0E0' },
    },
    yaxis: {
      title: {
        text: 'Performance Metrics',
        style: { fontSize: '13px', fontWeight: 600, color: '#263238' },
      },
      labels: {
        style: { fontSize: '11px', fontWeight: 500, colors: '#546E7A' },
      },
    },
    tooltip: {
      theme: 'light',
      shared: true,
      intersect: false,
      y: { formatter: (val: number) => val + ' entries' },
      style: { fontSize: '12px' },
      marker: { show: true },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '13px',
      fontWeight: 600,
      offsetY: 0,
      markers: { shape: 'circle' },
      itemMargin: { horizontal: 15, vertical: 5 },
    },
    grid: {
      show: true,
      borderColor: '#F1F1F1',
      strokeDashArray: 4,
      position: 'back',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 10, bottom: 0, left: 10 },
    },
    colors: ['#10295A'],
  };

  donutChart = {
    type: ChartType.PieChart,
    data: [] as any[],
    columnNames: ['Format Type', 'Count'],
    options: {
      pieHole: 0.65,
      colors: ['#2E3B55', '#E5E5E5'],
      legend: 'none',
      pieSliceText: 'none',
      tooltip: { trigger: 'focus' },
      chartArea: { width: '100%', height: '100%' },
      pieSliceBorderWidth: 3,
      pieSliceTextStyle: { fontSize: 0 },
      animation: {
        startup: true,
        duration: 1000,
        easing: 'inAndOut',
      },
    },
  };
  columns = [
    {
      key: 'contestName',
      label: 'addContestsTable.contestName',
      width: '200px',
    },
    {
      key: 'contestFormat',
      label: 'addContestsTable.contestFormat',
      width: '200px',
    },
    {
      key: 'contestType',
      label: 'addContestsTable.contestType',
      width: '200px',
    },
    {
      key: 'numberOfLevels',
      label: 'addContestsTable.numberOfLevels',
      width: '200px',
    },
    { key: 'location', label: 'addContestsTable.location', width: '200px' },
    { key: 'prize', label: 'addContestsTable.prize', width: '300px' },
    { key: 'totalViews', label: 'addContestsTable.totalViews', width: '150px' },
    { key: 'totalJoins', label: 'addContestsTable.totalJoins', width: '150px' },
    {
      key: 'completionRate',
      label: 'addContestsTable.completionRate',
      width: '200px',
    },
    {
      key: 'numberOfWinners',
      label: 'addContestsTable.numberOfWinners',
      width: '200px',
    },
    { key: 'clicks', label: 'addContestsTable.clicks', width: '150px' },
    { key: 'gender', label: 'addContestsTable.gender', width: '120px' },
    {
      key: 'ageBreakdown',
      label: 'addContestsTable.ageBreakdown',
      width: '200px',
    },
  ];

  // Calculate date range based on filter type
  getDateRange(
    filter: 'week' | 'month' | 'year'
  ): { start_date: number; end_date: number } {
    const now = new Date();
    const endDate = now.getTime();
    let startDate: number;

    if (filter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      startDate = weekAgo.getTime();
    } else if (filter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setDate(now.getDate() - 30);
      startDate = monthAgo.getTime();
    } else {
      const yearAgo = new Date(now);
      yearAgo.setFullYear(now.getFullYear() - 1);
      startDate = yearAgo.getTime();
    }

    return { start_date: startDate, end_date: endDate };
  }
  // Modified getReport method
  getReport(startDate?: number, endDate?: number) {
    this.sharedService.showLoader();
    let params: any = {};
    if (startDate !== undefined && endDate !== undefined) {
      params = { start_date: startDate, end_date: endDate };
    }
    // Get previous period data for comparison
    const previousRange = this.getPreviousDateRange(this.selectedTimeFilter);
    // Fetch previous period data first
    this.pageService.getAll('ANALYSIS_URL_Reports', {
      start_date: previousRange.start_date,
      end_date: previousRange.end_date
    }).subscribe({
      next: (previousResponse: any) => {
        // Store previous period data
        this.previousData = {
          totalContests: previousResponse.length,
          activeContests: previousResponse.filter((c: any) => c.active).length,
          pendingContests: previousResponse.filter((c: any) => c.status === 'pending').length,
          totalViews: previousResponse.reduce((sum: number, item: any) => sum + (item.totalViews || 0), 0),
          totalClicks: previousResponse.reduce((sum: number, item: any) => sum + (item.clicks || 0), 0)
        };
        // Now fetch current period data
        this.pageService.getAll('ANALYSIS_URL_Reports', params).subscribe({
          next: (response: any) => {
            this.sharedService.hideLoader();
            this.fullReportData = response;
            this.listOfData = response
              .sort((a: any, b: any) => b.id - a.id)
              .slice(0, 5);
            const totalContests = response.length;
            const activeContests = response.filter((c: any) => c.active).length;
            const pendingContests = response.filter((c: any) => c.status === 'pending').length;
            const totalViews = response.reduce((sum: number, item: any) => sum + (item.totalViews || 0), 0);
            const totalClicks = response.reduce((sum: number, item: any) => sum + (item.clicks || 0), 0);

            // Calculate percentages
            const percentages = {
              totalContests: this.calculatePercentageChange(this.previousData.totalContests, totalContests),
              activeContests: this.calculatePercentageChange(this.previousData.activeContests, activeContests),
              pendingContests: this.calculatePercentageChange(this.previousData.pendingContests, pendingContests),
              totalViews: this.calculatePercentageChange(this.previousData.totalViews, totalViews),
              totalClicks: this.calculatePercentageChange(this.previousData.totalClicks, totalClicks)
            };

            this.analysis = this.analysis.map((item) => {
              switch (item.title) {
                case 'analytic2.totalContest':
                  return {
                    ...item,
                    number: totalContests,
                    percentage: percentages.totalContests
                  };
                case 'analytic2.activeContests':
                  return {
                    ...item,
                    number: activeContests,
                    percentage: percentages.activeContests
                  };
                case 'analytic2.pending':
                  return {
                    ...item,
                    number: pendingContests,
                    percentage: percentages.pendingContests
                  };
                case 'analytic2.totalViews':
                  return {
                    ...item,
                    number: totalViews,
                    displayNumber: this.formatNumber(totalViews),
                    percentage: percentages.totalViews
                  };
                case 'analytic2.totalClicks':
                  return {
                    ...item,
                    number: totalClicks,
                    displayNumber: this.formatNumber(totalClicks),
                    percentage: percentages.totalClicks
                  };
                default:
                  return item;
              }
            });
            this.analysis = [...this.analysis];
            this.updateChartByTimeFilter();

            const groupedByFormat = response.reduce((acc: any, curr: any) => {
              const format = curr.contestFormat;
              // Only include if format exists and is not 'unknown'
              if (format && format !== 'unknown') {
                acc[format] = (acc[format] || 0) + 1;
              }
              return acc;
            }, {});
            this.donutChart.data = Object.entries(groupedByFormat)
              .filter(([key]) => key !== 'unknown') // Extra safety filter
              .map(([key, value]) => [
                key === 'integrated'
                  ? 'Integrated'
                  : key === 'non-integrated'
                    ? 'Non-Integrated'
                    : key.charAt(0).toUpperCase() + key.slice(1),
                value,
              ]);

            const integratedCount = groupedByFormat['integrated'] || 0;
            const total = (Object.values(groupedByFormat) as number[]).reduce((a, b) => a + b, 0);
            this.activePercentage = total > 0 ? Math.round((integratedCount / total) * 100) : 0;
          },
          error: (error) => {
            this.sharedService.hideLoader();
            this.sharedService.createNotification('error', 'Failed to load contest data');
          },
        });
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to load previous period data');
      }
    });
  }
  // Calculate previous date range based on current filter
  getPreviousDateRange(filter: 'week' | 'month' | 'year'): { start_date: number; end_date: number } {
    const now = new Date();
    let startDate: number;
    let endDate: number;
    if (filter === 'week') {
      // Previous week: 14 days ago to 7 days ago
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      endDate = weekAgo.getTime();
      const twoWeeksAgo = new Date(now);
      twoWeeksAgo.setDate(now.getDate() - 14);
      startDate = twoWeeksAgo.getTime();
    } else if (filter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setDate(now.getDate() - 30);
      endDate = monthAgo.getTime();

      const twoMonthsAgo = new Date(now);
      twoMonthsAgo.setDate(now.getDate() - 60);
      startDate = twoMonthsAgo.getTime();
    } else {
      // Previous year: 2 years ago to 1 year ago
      const yearAgo = new Date(now);
      yearAgo.setFullYear(now.getFullYear() - 1);
      endDate = yearAgo.getTime();

      const twoYearsAgo = new Date(now);
      twoYearsAgo.setFullYear(now.getFullYear() - 2);
      startDate = twoYearsAgo.getTime();
    }
    return { start_date: startDate, end_date: endDate };
  }

  // Calculate percentage change
  calculatePercentageChange(previousValue: number, currentValue: number): number {
    // ✅ FIX #1: Agar dono zero hain, to 0 return karo (na + na -)
    if (previousValue === 0 && currentValue === 0) {
      return 0;
    }
    // ✅ FIX #2: Agar previous 0 tha aur ab value hai, to +100% (plus sign)
    if (previousValue === 0 && currentValue > 0) {
      return 100;
    }
    // ✅ FIX #3: Agar previous value thi aur ab 0 hai, to -100% (minus sign)
    if (previousValue > 0 && currentValue === 0) {
      return -100;
    }
    const change = ((currentValue - previousValue) / previousValue) * 100;
    const roundedChange = Math.round(change);
    // Cap at 100%
    if (roundedChange > 100) {
      return 100;
    } else if (roundedChange < -100) {
      return -100;
    }
    return roundedChange;
  }
  // Calculate pr
  changeTimeFilter(filter: 'week' | 'month' | 'year') {
    this.selectedTimeFilter = filter;
    this.timeFilterLabel =
      filter === 'week'
        ? 'This Week'
        : filter === 'month'
          ? 'This Month'
          : 'This Year';
    const dateRange = this.getDateRange(filter);
    this.getReport(dateRange.start_date, dateRange.end_date);
  }
  updateChartByTimeFilter() {
    let categories: string[] = [];
    let entriesData: number[] = [];
    if (this.selectedTimeFilter === 'week') {
      const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      categories = daysOfWeek;
      entriesData = this.getEntriesByDays(7);
    } else if (this.selectedTimeFilter === 'month') {
      categories = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
      entriesData = this.getEntriesByWeeks(4);
    } else if (this.selectedTimeFilter === 'year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',];
      categories = months;
      entriesData = this.getEntriesByMonths(12);
    }
    this.barChart = {
      ...this.barChart,
      series: [{ name: 'Total Entries', data: entriesData }],
      xaxis: {
        ...this.barChart.xaxis,
        categories,
        labels: {
          ...this.barChart.xaxis?.labels,
          rotate: this.selectedTimeFilter === 'week' ? 0 : -45,
        },
      },
    };
  }
  getEntriesByDays(dayCount: number): number[] {
    const today = new Date();
    const entriesByDay: number[] = new Array(dayCount).fill(0);
    this.fullReportData.forEach((item: any) => {
      if (item.startDate) {
        const itemDate = new Date(item.startDate);
        const diffTime = today.getTime() - itemDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays < dayCount) {
          const dayIndex = (today.getDay() - diffDays + 7) % 7;
          const mondayBasedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
          entriesByDay[mondayBasedIndex] += item.totalJoins || 1;
        }
      }
    });
    return entriesByDay;
  }
  getEntriesByWeeks(weekCount: number): number[] {
    const entriesByWeek: number[] = new Array(weekCount).fill(0);
    const today = new Date();
    this.fullReportData.forEach((item: any) => {
      if (item.startDate) {
        const itemDate = new Date(item.startDate);
        const diffTime = today.getTime() - itemDate.getTime();
        const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
        if (diffWeeks >= 0 && diffWeeks < weekCount) {
          entriesByWeek[weekCount - 1 - diffWeeks] += item.totalJoins || 1;
        }
      }
    });
    return entriesByWeek;
  }
  getEntriesByMonths(monthCount: number): number[] {
    const entriesByMonth: number[] = new Array(monthCount).fill(0);
    this.fullReportData.forEach((item: any) => {
      if (item.startDate) {
        const itemDate = new Date(item.startDate);
        const monthIndex = itemDate.getMonth();
        entriesByMonth[monthIndex] += item.totalJoins || 1;
      }
    });
    return entriesByMonth;
  }
  formatNumber(value: number): string {
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (value >= 1_000) {
      return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return value.toString();
  }
  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => {
      if (acc && typeof acc === 'object') {
        return acc[part];
      }
      return undefined;
    }, obj);
  }
  viewDetail(id: number) {
    this.router.navigate(['/home/analytic/monitoring'], {
      queryParams: { contest_id: id },
    });
  }
  flattenData(data: any[]): any[] {
    return data.map((item) => ({
      id: item.id,
      contestName: item.contestName || '',
      contestFormat: item.contestFormat || '',
      contestType: item.contestType || '',
      numberOfLevels: item.numberOfLevels || 0,
      location: item.location || '',
      prize: item.prize || '',
      totalViews: item.totalViews || 0,
      totalJoins: item.totalJoins || 0,
      completionRate: item.completionRate || 0,
      numberOfWinners: item.numberOfWinners || 0,
      rewardStatus: item.rewardStatus || '',
      clicks: item.clicks || 0,
      gender: item.gender || '',
      ageBreakdown: item.ageBreakdown || '',
      locationHeatmap: item.locationHeatmap || '',
      status: item.status || '',
      startDate: item.startDate || '',
      endDate: item.endDate || '',
      prizeAnnounceDate: item.prizeAnnounceDate || '',
      totalPoints: item.totalPoints || 0,
      active: item.active ? 'Yes' : 'No',
    }));
  }
  // ==================== PDF DOWNLOAD METHOD ====================
  
// ==================== HELPER METHOD FOR TIMELINE ====================
getTimelineStatus(endDate: string): { timeline: string; status: string } {
  if (!endDate) {
    return { timeline: 'N/A', status: 'N/A' };
  }
  
  const currentDate = new Date();
  const contestEndDate = new Date(endDate);
  const timeDiff = contestEndDate.getTime() - currentDate.getTime();
  
  // If end date is in the past
  if (timeDiff < 0) {
    return { timeline: 'Expired', status: 'Expired' };
  }
  
  // Calculate remaining time
  const daysRemaining = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hoursRemaining = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  
  let timeline = '';
  if (daysRemaining > 0) {
    timeline = `${daysRemaining}d`;
  } else if (hoursRemaining > 0) {
    timeline = `${hoursRemaining}h`;
  } else if (minutesRemaining > 0) {
    timeline = `${minutesRemaining}m`;
  } else {
    timeline = 'Ending Soon';
  }
  
  return { timeline, status: 'Active' };
}

// ==================== PDF DOWNLOAD METHOD ====================
async downloadPDF() {
  const dataToExport = this.displayData;
  if (!dataToExport || dataToExport.length === 0) {
    this.sharedService.createNotification('error', 'No Contest Data Found');
    return;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const isAr = isArabicLang();
  const arabicFontLoaded = await ensureArabicFont(doc);

  const labels = {
    title: isAr ? 'تقرير مراقبة حملات لُقطة' : 'Luqta Campaign Monitoring Report',
    id: isAr ? 'المعرف' : 'ID',
    contestName: isAr ? 'اسم الحملة' : 'Campaign Name',
    format: isAr ? 'الصيغة' : 'Format',
    type: isAr ? 'النوع' : 'Type',
    levels: isAr ? 'المستويات' : 'Levels',
    location: isAr ? 'الموقع' : 'Location',
    prize: isAr ? 'الجائزة' : 'Prize',
    gender: isAr ? 'الجنس' : 'Gender',
    age: isAr ? 'العمر' : 'Age',
    timeline: isAr ? 'الجدول الزمني' : 'Timeline',
    status: isAr ? 'الحالة' : 'Status',
    preview: isAr ? 'معاينة' : 'Preview',
    statusActive: isAr ? 'نشط' : 'Active',
    statusExpired: isAr ? 'منتهي' : 'Expired',
    pageOf: (page: number, total: number) =>
      isAr ? `صفحة ${page} من ${total}` : `Page ${page} of ${total}`,
  };

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let currentY = 15;

  const logoData = await this.sharedService.getLuqtaLogoDataUrl();
  if (logoData) {
    doc.addImage(logoData, 'PNG', 10, 8, 10, 10);
  }

  // Header Section
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  drawBilingualText(doc, labels.title, pageWidth / 2, currentY, arabicFontLoaded, {
    align: 'center',
  });
  currentY += 15;

  // Prepare table data with timeline and status
  const bodyRows = dataToExport.map((item: any) => {
    const { timeline, status } = this.getTimelineStatus(item.endDate);
    return [
      item.id || 'N/A',
      item.contestName || 'N/A',
      item.contestFormat || 'N/A',
      item.contestType || 'N/A',
      item.numberOfLevels || '0',
      item.location || 'N/A',
      item.prize || 'N/A',
      item.gender || 'N/A',
      item.ageBreakdown || 'All',
      timeline,
      status,
      '', // Preview — drawn as an image in didDrawCell
    ];
  });
  const previewColIndex = 11;
  const clientLogoData = await this.sharedService.getClientLogoDataUrl();
  const previewDataUrls = await Promise.all(
    dataToExport.map((item: any) => {
      const bannerUrl = item?.banners?.[0]?.url || item?.banners?.[0]?.path || item?.bannerUrl || item?.banner || '';
      return bannerUrl ? this.sharedService.getImageDataUrl(bannerUrl) : Promise.resolve(clientLogoData);
    })
  );

  const headRow = [
    labels.id,
    labels.contestName,
    labels.format,
    labels.type,
    labels.levels,
    labels.location,
    labels.prize,
    labels.gender,
    labels.age,
    labels.timeline,
    labels.status,
    labels.preview,
  ].map((h) => (isAr ? reshapeArabic(h) : h));

  autoTable(doc, {
    startY: currentY,
    head: [headRow],
    body: bodyRows,
    styles: {
      fontSize: 8,
      cellPadding: 2.5,
      overflow: 'linebreak',
      cellWidth: 'wrap',
      halign: 'center',
      valign: 'middle',
      minCellHeight: 14, // keeps the Preview thumbnail column legible on short rows
    },
    headStyles: {
      fillColor: [44, 62, 80],
      textColor: 255,
      fontStyle: 'bold',
      halign: isAr ? 'right' : 'center',
      valign: 'middle',
      fontSize: 9,
      font: arabicFontLoaded ? 'Amiri' : 'helvetica',
    },
    bodyStyles: {
      textColor: 50,
      halign: 'center',
      valign: 'middle',
    },
    didParseCell: (data: any) => {
      arabicAwareCellHook(arabicFontLoaded)(data);
      if (data.section === 'body' && data.column.index === 10) {
        const raw = String(data.cell.raw ?? '');
        if (raw === 'Expired') {
          data.cell.text = [labels.statusExpired];
        } else if (raw === 'Active') {
          data.cell.text = [labels.statusActive];
        }
      }
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 15 },  // ID
      1: { cellWidth: 35 },  // Contest Name
      2: { cellWidth: 25 },  // Format
      3: { cellWidth: 20 },  // Type
      4: { cellWidth: 15 },  // Levels
      5: { cellWidth: 30 },  // Location
      6: { cellWidth: 35 },  // Prize
      7: { cellWidth: 18 },  // Gender
      8: { cellWidth: 18 },  // Age
      9: { cellWidth: 20 },  // Timeline
      10: { cellWidth: 20 }, // Status
      11: { cellWidth: 18 }, // Preview
    },
    didDrawCell: (data: any) => {
      // CONTEST PREVIEW (banner thumbnail)
      if (data.section === 'body' && data.column.index === previewColIndex) {
        const previewData = previewDataUrls[data.row.index];
        if (previewData) {
          const padding = 1;
          const imgSize = Math.min(data.cell.width, data.cell.height) - padding * 2;
          const x = data.cell.x + (data.cell.width - imgSize) / 2;
          const y = data.cell.y + (data.cell.height - imgSize) / 2;
          try {
            doc.addImage(previewData, 'JPEG', x, y, imgSize, imgSize);
          } catch {
            // Skip silently if the image data is unusable.
          }
        }
      }

      // Color code the Status column
      if (data.section === 'body' && data.column.index === 10) {
        const status = data.cell.raw;
        if (status === 'Expired') {
          doc.setFillColor(255, 99, 71); // Red for expired
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
          doc.setTextColor(255, 255, 255);
          drawBilingualText(
            doc,
            labels.statusExpired,
            data.cell.x + data.cell.width / 2,
            data.cell.y + data.cell.height / 2,
            arabicFontLoaded,
            { align: 'center', baseline: 'middle' }
          );
        } else if (status === 'Active') {
          doc.setFillColor(46, 204, 113); // Green for active
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
          doc.setTextColor(255, 255, 255);
          drawBilingualText(
            doc,
            labels.statusActive,
            data.cell.x + data.cell.width / 2,
            data.cell.y + data.cell.height / 2,
            arabicFontLoaded,
            { align: 'center', baseline: 'middle' }
          );
        }
      }
    },
    margin: { top: 5, left: 10, right: 10 },
    didDrawPage: function (data) {
      const pageCount = doc.internal.pages.length - 1;
      doc.setFontSize(9);
      doc.setTextColor(100);
      drawBilingualText(
        doc,
        labels.pageOf(data.pageNumber, pageCount),
        pageWidth / 2,
        pageHeight - 10,
        arabicFontLoaded,
        { align: 'center' }
      );
    },
  });

  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `Contest_Monitoring_Report_${timestamp}.pdf`;
  doc.save(filename);
  
  this.sharedService.createNotification(
    'success',
    'PDF downloaded successfully'
  );
}

// ==================== EXCEL DOWNLOAD METHOD ====================
downloadExcel() {
  const isAr = isArabicLang();
  const dataToExport = this.displayData;
  if (!dataToExport || dataToExport.length === 0) {
    this.sharedService.createNotification('error', isAr ? 'لا توجد بيانات مسابقة' : 'No Contest Data Found');
    return;
  }

  // Prepare data for Excel
  const excelData = dataToExport.map((item: any) => {
    const { timeline, status } = this.getTimelineStatus(item.endDate);
    if (isAr) {
      return {
        'المعرف': item.id || 'غير متوفر',
        'اسم المسابقة': item.contestName || 'غير متوفر',
        'الصيغة': item.contestFormat || 'غير متوفر',
        'النوع': item.contestType || 'غير متوفر',
        'المستويات': item.numberOfLevels || '0',
        'الموقع': item.location || 'غير متوفر',
        'الجائزة': item.prize || 'غير متوفر',
        'إجمالي المشاهدات': item.totalViews || 0,
        'إجمالي المشاركات': item.totalJoins || 0,
        'معدل الإكمال': item.completionRate || 0,
        'الفائزون': item.numberOfWinners || 0,
        'النقرات': item.clicks || 0,
        'الجنس': item.gender || 'غير متوفر',
        'الفئة العمرية': item.ageBreakdown || 'الكل',
        'الجدول الزمني': timeline,
        'الحالة': status,
      };
    }
    return {
      'ID': item.id || 'N/A',
      'Contest Name': item.contestName || 'N/A',
      'Format': item.contestFormat || 'N/A',
      'Type': item.contestType || 'N/A',
      'Levels': item.numberOfLevels || '0',
      'Location': item.location || 'N/A',
      'Prize': item.prize || 'N/A',
      'Total Views': item.totalViews || 0,
      'Total Joins': item.totalJoins || 0,
      'Completion Rate': item.completionRate || 0,
      'Winners': item.numberOfWinners || 0,
      'Clicks': item.clicks || 0,
      'Gender': item.gender || 'N/A',
      'Age Breakdown': item.ageBreakdown || 'All',
      'Timeline': timeline,
      'Status': status,
    };
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  if (isAr) {
    worksheet['!views'] = [{ RTL: true }];
  }

  // Set column widths
  const columnWidths = [
    { wch: 8 },   // ID
    { wch: 25 },  // Contest Name
    { wch: 15 },  // Format
    { wch: 15 },  // Type
    { wch: 8 },   // Levels
    { wch: 20 },  // Location
    { wch: 20 },  // Prize
    { wch: 14 },  // Total Views
    { wch: 14 },  // Total Joins
    { wch: 15 },  // Completion Rate
    { wch: 10 },  // Winners
    { wch: 10 },  // Clicks
    { wch: 10 },  // Gender
    { wch: 15 },  // Age Breakdown
    { wch: 14 },  // Timeline
    { wch: 12 },  // Status
  ];
  worksheet['!cols'] = columnWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  const sheetName = isAr ? 'تقرير المسابقات' : 'Contest Report';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  // Save file
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = isAr ? `تقرير_مراقبة_المسابقات_${timestamp}.xlsx` : `Contest_Monitoring_Report_${timestamp}.xlsx`;
  saveAs(blob, filename);

  this.sharedService.createNotification(
    'success',
    isAr ? 'تم تنزيل ملف Excel بنجاح' : 'Excel downloaded successfully'
  );
}

  onMouseEnter(event: MouseEvent, value: string) {
    if (!value || value.length <= 12) return;
    const container = event.currentTarget as HTMLElement;
    const tooltip = container.querySelector('.custom-tooltip') as HTMLElement;
    if (tooltip) {
      const rect = container.getBoundingClientRect();
      const left = rect.left + (rect.width / 2);
      const top = rect.top - 8;
      tooltip.style.position = 'fixed';
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.style.transform = 'translate(-50%, -100%)';
      tooltip.style.visibility = 'visible';
      tooltip.style.opacity = '1';
      tooltip.style.zIndex = '9999';
    }
  }
  onMouseLeave(event: MouseEvent) {
    const container = event.currentTarget as HTMLElement;
    const tooltip = container.querySelector('.custom-tooltip') as HTMLElement;
    if (tooltip) {
      tooltip.style.visibility = 'hidden';
      tooltip.style.opacity = '0';
    }
  }
}
